import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authFetch } from '../services/authFetch';
import XPInfoModal from '../components/XPInfoModal';
import RankShareModal from '../components/RankShareModal';
import Avatar from '../components/Avatar';
import './Leaderboard.css';

type Window = 'weekly' | 'alltime';
type Tab = 'overall' | 'subjects';

interface Row {
  rank: number;
  student_id: string;
  name: string;
  telegram_username: string | null;
  stream: string | null;
  school: string | null;
  avatar_url: string | null;
  initial: string;
  xp: number;
  questions_total: number;
}

interface MyRank {
  rank: number;
  xp: number;
  total_ranked: number;
}

// Date-range metadata returned by every leaderboard endpoint so the UI can
// show *exactly* which window a board covers (kills the "weekly looks like my
// total" confusion). All timestamps are UTC ISO; we render them in EAT.
interface WindowMeta {
  as_of?: string;
  window_start?: string | null;
  window_end?: string | null;
}

interface OverallResponse extends WindowMeta {
  window: Window;
  top: Row[];
  me: MyRank | null;
  neighbours: Row[];
  is_private_rank?: boolean;
}

interface SubjectBoard {
  subject: string;
  top: Row[];
  my_rank: MyRank | null;
}

interface SubjectsResponse extends WindowMeta {
  window: Window;
  subjects: SubjectBoard[];
}

interface ComboRow {
  rank: number;
  student_id: string;
  name: string;
  telegram_username: string | null;
  stream: string | null;
  avatar_url: string | null;
  initial: string;
  combo: number;
}

interface CombosResponse extends WindowMeta {
  window: Window;
  top: ComboRow[];
  me: { rank: number; lifetime_combo: number; total_ranked: number } | null;
}

// Subjects we surface as tabs. Mirrors API's SUBJECT_TABS — order = display order.
const SUBJECTS = [
  'Biology', 'English', 'Mathematics', 'Aptitude', 'History',
  'Chemistry', 'Physics', 'Economics', 'Geography',
];

// Format an ISO timestamp as an EAT "Mon D" date (e.g. "Jun 4"). Ethiopian
// students are UTC+3 — pin the timezone so the label matches the bot exactly,
// regardless of the device's own clock/locale.
const eatDate = (iso?: string | null): string =>
  new Date(iso || Date.now()).toLocaleDateString('en-US', {
    timeZone: 'Africa/Addis_Ababa', month: 'short', day: 'numeric',
  });

// Short pill label for each window.
const windowName = (window: Window): string =>
  window === 'weekly' ? 'Last 7 days' : 'All-time';

// Sub-label shown after each board title: the *actual* dates so "weekly" is
// never mistaken for a lifetime total. Prefers server window metadata
// (rolling 7d = utcnow()-7d), falls back to a client estimate.
const rangeLabel = (window: Window, meta?: WindowMeta): string => {
  if (window === 'weekly') {
    if (meta?.window_start && meta?.window_end) {
      return `${eatDate(meta.window_start)} – ${eatDate(meta.window_end)}`;
    }
    const e = new Date(); const s = new Date(); s.setDate(e.getDate() - 7);
    return `${eatDate(s.toISOString())} – ${eatDate(e.toISOString())}`;
  }
  return `as of ${eatDate(meta?.as_of)}`;
};

const SUBJECT_EMOJI: Record<string, string> = {
  Biology: '🧬', Chemistry: '⚗️', Physics: '⚛️', Mathematics: '📐',
  Geography: '🌍', History: '🏛️', Economics: '💰',
  English: '📖', Aptitude: '🧠',
};

const Leaderboard: React.FC = () => {
  const { student } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState<Tab>('overall');
  // Default to All-time so a student lands on their *real* standing — weekly is
  // an opt-in competitive view. (A student once thought weekly = his total and
  // that we'd "discounted" his points.)
  const [window, setWindow] = useState<Window>('alltime');
  const [overall, setOverall] = useState<OverallResponse | null>(null);
  const [subjects, setSubjects] = useState<SubjectsResponse | null>(null);
  const [combos, setCombos] = useState<CombosResponse | null>(null);
  const [streakDays, setStreakDays] = useState(0);
  const [activeSubject, setActiveSubject] = useState<string>('Biology');
  const [loading, setLoading] = useState(false);
  const [showXpInfo, setShowXpInfo] = useState(false);
  const [showShare, setShowShare] = useState(false);
  // When the user taps "Share my rank in {subject}", the share modal flips
  // to the subject variant. null = share my overall rank.
  const [shareSubject, setShareSubject] = useState<string | null>(null);

  // Overall fetch (XP + combos in parallel — both sit on the Overall tab)
  useEffect(() => {
    if (tab !== 'overall') return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [boardRes, comboRes] = await Promise.all([
          authFetch(`/api/gamification/leaderboard?window=${window}&n=20`),
          // Best runs = longest streak ever, an inherently all-time achievement.
          // Always fetch all-time regardless of the window toggle above.
          authFetch(`/api/gamification/combos?window=alltime&n=20`),
        ]);
        if (boardRes.ok && !cancelled) {
          const data: OverallResponse = await boardRes.json();
          setOverall(data);
        }
        if (comboRes.ok && !cancelled) {
          const data: CombosResponse = await comboRes.json();
          setCombos(data);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tab, window]);

  // Subjects fetch
  useEffect(() => {
    if (tab !== 'subjects') return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await authFetch(`/api/gamification/subjects?window=${window}&n=5`);
        if (!res.ok) return;
        const data: SubjectsResponse = await res.json();
        if (!cancelled) setSubjects(data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tab, window]);

  // Streak — used by the rank share card. Cheap call, fire once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch('/api/gamification/my-rank');
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data?.streak) {
          setStreakDays(data.streak.alive ? data.streak.current : 0);
        }
      } catch {
        // Non-critical — share card just omits the streak tile when 0.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Best subject for the share card — pick the subject where the student
  // has their lowest (best) rank. Falls back to undefined if no qualifying
  // subject rank yet.
  const topSubjectForCard: string | undefined = (() => {
    if (!subjects?.subjects) return undefined;
    let best: { subject: string; rank: number } | null = null;
    for (const board of subjects.subjects) {
      if (!board.my_rank) continue;
      if (best === null || board.my_rank.rank < best.rank) {
        best = { subject: board.subject, rank: board.my_rank.rank };
      }
    }
    return best?.subject;
  })();

  const shareUrl = typeof window !== 'undefined' && (window as any).location
    ? `${(window as any).location.origin}`
    : 'https://lomistudy.com';

  const myStudentId = student?.student_id ? String(student.student_id) : null;
  const isMe = (row: Row) => myStudentId !== null && row.student_id === myStudentId;
  const meInTop = useMemo(() => {
    if (!overall?.top || !myStudentId) return false;
    return overall.top.some(r => r.student_id === myStudentId);
  }, [overall, myStudentId]);

  const activeSubjectBoard = subjects?.subjects.find(s => s.subject === activeSubject);

  return (
    <div className="leaderboard-page">
      <header className="lb-header">
        <button className="lb-back" onClick={() => navigate('/dashboard')} aria-label="Back to dashboard">
          ←
        </button>
        <h1 className="lb-brand-title">
          <img src="/logo192.png" alt="" className="lb-brand-logo" width={28} height={28} />
          <span>Lomi <span className="lb-brand-amh">ሎሚ</span></span>
          <span className="lb-brand-divider" aria-hidden="true">·</span>
          <span className="lb-brand-sub">Leaderboard</span>
        </h1>
        <div className="lb-spacer" />
      </header>

      <div className="lb-tabs" role="tablist">
        <button
          className={`lb-tab ${tab === 'overall' ? 'active' : ''}`}
          onClick={() => setTab('overall')}
          role="tab"
          aria-selected={tab === 'overall'}
        >
          Overall
        </button>
        <button
          className={`lb-tab ${tab === 'subjects' ? 'active' : ''}`}
          onClick={() => setTab('subjects')}
          role="tab"
          aria-selected={tab === 'subjects'}
        >
          By Subject
        </button>
      </div>

      <div className="lb-window-toggle">
        <button
          className={`lb-pill ${window === 'alltime' ? 'active' : ''}`}
          onClick={() => setWindow('alltime')}
        >
          All-time
        </button>
        <button
          className={`lb-pill ${window === 'weekly' ? 'active' : ''}`}
          onClick={() => setWindow('weekly')}
        >
          Last 7 days
        </button>
      </div>

      {tab === 'overall' && (
        <div className="lb-section lb-section-overall">
          {/* My rank hero strip */}
          {overall?.me ? (
            <div className="lb-me-strip">
              <div className="lb-me-rank">
                <div className="lb-me-label">
                  Your rank · {windowName(window)}
                  {overall.is_private_rank && <span className="lb-private-pill">PRIVATE</span>}
                </div>
                <div className="lb-me-value">#{overall.me.rank}</div>
              </div>
              <div className="lb-me-xp">
                <div className="lb-me-label">Points</div>
                <div className="lb-me-value">{overall.me.xp.toLocaleString()}</div>
              </div>
              <div className="lb-me-actions">
                <button
                  className="lb-cta"
                  onClick={() => navigate('/micro-learning', {
                    state: {
                      // Land on a useful subject so the page doesn't render
                      // its empty "pick a subject" welcome state. Falls
                      // back to the student's stream-default subject if no
                      // ranked subject is known yet.
                      defaultSubject: topSubjectForCard
                        || (student?.subjects && student.subjects[0])
                        || (student?.stream === 'social' ? 'History' : 'Biology'),
                      defaultTab: 'past_exams',
                    },
                  })}
                >
                  Practice now →
                </button>
                <button
                  className="lb-share-btn"
                  onClick={() => { setShareSubject(null); setShowShare(true); }}
                  aria-label="Share my rank"
                >
                  ↗ Share
                </button>
              </div>
            </div>
          ) : !loading ? (
            <div className="lb-empty-me">
              <p>Answer your first question to enter the rankings.</p>
              <button
                className="lb-cta"
                onClick={() => navigate('/micro-learning', {
                  state: {
                    defaultSubject: (student?.subjects && student.subjects[0])
                      || (student?.stream === 'social' ? 'History' : 'Biology'),
                    defaultTab: 'past_exams',
                  },
                })}
              >
                Start practicing →
              </button>
            </div>
          ) : null}

          {/* Two-column grid on desktop: left = main + neighbours, right = combos */}
          <div className="lb-grid">
            <div className="lb-grid-left">
              {/* Top board */}
              <div className="lb-board">
                <div className="lb-board-title">
                  Top 20
                  <span className="lb-board-range">· {rangeLabel(window, overall ?? undefined)}</span>
                </div>
                {loading && !overall ? (
                  <SkeletonRows n={10} />
                ) : (
                  <ol className="lb-rows">
                    {overall?.top.map(row => (
                      <Entry key={row.student_id} row={row} highlight={isMe(row)} />
                    ))}
                  </ol>
                )}
              </div>

              {/* Near-you section if I'm beyond top */}
              {!meInTop && overall?.neighbours && overall.neighbours.length > 0 && (
                <div className="lb-board lb-near-you">
                  <div className="lb-board-title">Near you</div>
                  <ol className="lb-rows">
                    {overall.neighbours.map(row => (
                      <Entry key={row.student_id} row={row} highlight={isMe(row)} />
                    ))}
                  </ol>
                </div>
              )}
            </div>

            <div className="lb-grid-right">
          {/* Combos — second hero metric. Only renders when there's enough
              qualifying activity (≥3 students with a 3+ run). */}
          {combos && combos.top.length >= 3 && (
            <div className="lb-board lb-combos">
              <div className="lb-board-title">
                <span className="lb-board-emoji">⚡</span>
                Best runs — most-correct in a row
                <span className="lb-board-range">· All-time</span>
              </div>
              <ol className="lb-rows">
                {combos.top.map(row => {
                  const isSelf = !!myStudentId && row.student_id === myStudentId;
                  return (
                    <li
                      key={row.student_id}
                      className={`lb-row ${isSelf ? 'lb-row-me' : ''}`}
                    >
                      <span className="lb-row-rank">
                        {row.rank <= 3 ? MEDALS[row.rank - 1] : `${row.rank}.`}
                      </span>
                      <Avatar url={row.avatar_url} name={row.initial || row.name} size={28} className="lb-row-avatar" />
                      <span className="lb-row-name">
                        {row.name}
                        {isSelf && <span className="lb-you-pill">YOU</span>}
                      </span>
                      <span className="lb-row-xp">⚡ {row.combo}</span>
                    </li>
                  );
                })}
              </ol>
              {combos.me && combos.me.rank > combos.top.length && (
                <div className="lb-me-mini">
                  Your best combo: <strong>⚡ {combos.me.lifetime_combo}</strong>
                  <span className="lb-me-mini-meta">
                    {' '}— rank #{combos.me.rank}
                  </span>
                </div>
              )}
              {!combos.me && (
                <div className="lb-me-mini lb-me-mini-empty">
                  Hit 3 correct in a row in any session to enter this board.
                </div>
              )}
            </div>
          )}
            </div>
          </div>
        </div>
      )}

      {tab === 'subjects' && (
        <div className="lb-section">
          {/* Subject chip picker — tap, no typing */}
          <div className="lb-chip-row" role="tablist">
            {SUBJECTS.map(s => (
              <button
                key={s}
                className={`lb-chip ${activeSubject === s ? 'active' : ''}`}
                onClick={() => setActiveSubject(s)}
                role="tab"
                aria-selected={activeSubject === s}
              >
                <span className="lb-chip-emoji">{SUBJECT_EMOJI[s] || '📚'}</span>
                <span>{s}</span>
              </button>
            ))}
          </div>

          {/* Active subject's mini-board */}
          {activeSubjectBoard && (
            <div className="lb-board">
              <div className="lb-board-title">
                <span className="lb-board-emoji">{SUBJECT_EMOJI[activeSubject] || '📚'}</span>
                Top in {activeSubject}
                <span className="lb-board-range">· {rangeLabel(window, subjects ?? undefined)}</span>
              </div>

              {activeSubjectBoard.my_rank ? (
                <div className="lb-me-mini">
                  <span>
                    Your rank in {activeSubject}: <strong>#{activeSubjectBoard.my_rank.rank}</strong>
                    <span className="lb-me-mini-meta">
                      {' · '}{activeSubjectBoard.my_rank.xp.toLocaleString()} pts
                    </span>
                  </span>
                  <button
                    type="button"
                    className="lb-share-btn lb-share-btn-inline"
                    onClick={() => { setShareSubject(activeSubject); setShowShare(true); }}
                    aria-label={`Share my rank in ${activeSubject}`}
                  >
                    ↗ Share
                  </button>
                </div>
              ) : (
                <div className="lb-me-mini lb-me-mini-empty">
                  Not yet ranked in {activeSubject} — answer some {activeSubject} questions to enter.
                </div>
              )}

              {loading && activeSubjectBoard.top.length === 0 ? (
                <SkeletonRows n={5} />
              ) : activeSubjectBoard.top.length === 0 ? (
                <div className="lb-empty-board">
                  No one has earned {activeSubject} points {window === 'weekly' ? 'in the last 7 days' : 'yet'}.
                </div>
              ) : (
                <ol className="lb-rows">
                  {activeSubjectBoard.top.map(row => (
                    <Entry key={row.student_id} row={row} highlight={isMe(row)} />
                  ))}
                </ol>
              )}
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        className="lb-footer-tip lb-footer-tip-button"
        onClick={() => setShowXpInfo(true)}
      >
        How points work <span className="lb-footer-tip-arrow">ⓘ</span>
      </button>

      <XPInfoModal open={showXpInfo} onClose={() => setShowXpInfo(false)} />

      {overall?.me && (() => {
        // Resolve subject variant payload when sharing a subject rank.
        const subjBoard = shareSubject
          ? subjects?.subjects.find(s => s.subject === shareSubject)
          : undefined;
        const subjMe = subjBoard?.my_rank;
        return (
          <RankShareModal
            open={showShare}
            onClose={() => setShowShare(false)}
            card={{
              studentName: (student?.profile_name || student?.first_name || 'Lomi Scholar').toString(),
              school: (student?.school_name || undefined) as string | undefined,
              gradeLevel: (student?.grade_level ?? null) as number | null,
              avatarUrl: (student?.avatar_url || null),
              rank: overall.me.rank,
              totalRanked: overall.me.total_ranked,
              xp: subjMe?.xp ?? overall.me.xp,
              streakDays,
              bestCombo: combos?.me?.lifetime_combo || 0,
              topSubject: topSubjectForCard,
              url: shareUrl,
              variant: shareSubject ? 'subject' : 'overall',
              subjectName: shareSubject || undefined,
              subjectRank: subjMe?.rank,
              subjectTotalRanked: subjMe?.total_ranked,
            }}
          />
        );
      })()}
    </div>
  );
};

const MEDALS = ['🥇', '🥈', '🥉'];

const Entry: React.FC<{ row: Row; highlight: boolean }> = ({ row, highlight }) => {
  const prefix = row.rank <= 3 ? MEDALS[row.rank - 1] : `${row.rank}.`;
  return (
    <li className={`lb-row ${highlight ? 'lb-row-me' : ''}`}>
      <span className="lb-row-rank">{prefix}</span>
      <Avatar url={row.avatar_url} name={row.initial || row.name} size={28} className="lb-row-avatar" />
      <span className="lb-row-name">
        {row.name}
        {highlight && <span className="lb-you-pill">YOU</span>}
      </span>
      <span className="lb-row-xp">{row.xp.toLocaleString()} pts</span>
    </li>
  );
};

const SkeletonRows: React.FC<{ n: number }> = ({ n }) => (
  <ol className="lb-rows">
    {Array.from({ length: n }).map((_, i) => (
      <li key={i} className="lb-row lb-row-skeleton">
        <span className="lb-row-rank">{i + 1}.</span>
        <span className="lb-row-name"><span className="lb-skel-bar" /></span>
        <span className="lb-row-xp"><span className="lb-skel-bar" /></span>
      </li>
    ))}
  </ol>
);

export default Leaderboard;
